import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import gql from 'graphql-tag';
import jwt_decode from "jwt-decode";
import { css } from "@emotion/react";
import { trim } from "lodash";
import { useMutation } from '@apollo/react-hooks';
import EmailValidator from 'email-validator';

import Form from "../form/Form";
import LabelledField from "../form/LabelledField";
import Input from "../form/Input";
import FormActions from "../form/FormActions";
import Button from "../Button";
import useForm, { FieldList } from "../../hooks/useForm";
import { login, needEmailVerification, socialLogin, SocialLoginType } from "../../includes/auth";
import errorParser from '../../includes/errorParser';
import { useAppState } from "../../contexts/AppContext";
import SocialLogin from "../social/SocialLogin";
import Loading from "../utils/Loading";
import theme from "../../../theme";
import TwoColumn from "../form/TwoColumn";
import useSetState from "../../hooks/useSetState";
import ErrorMessage from "../errors/ErrorMessage";
import * as storage from "../../includes/localStorage";
import useElementDimensions from "../../hooks/useElementDimensions";
import { LOGIN_URL } from "../../../config/config";
import { HomeUser } from "../utils/UserSession";

type Props = {
    redirectAfterSignUp: (user: any) => void,
};

type DefaultState = {
    isSubmitting: boolean,
    errorMessage: string | null,
    isVisibleErrorMessage: boolean,
    areRequirementsVisible: boolean,
    socialButtonKey: number,
}

const INITIAL_STATE = {
    isSubmitting: false,
    errorMessage: "The provided details don't seem to be correct. Please try again.",
    isVisibleErrorMessage: false,
    areRequirementsVisible: false,
    socialButtonKey: Math.random(),
}

let MIN_PASSWORD_LENGTH = 10;

const SignUp: React.FC<Props> = ({ redirectAfterSignUp }) => {
    const [appState, setAppState] = useAppState();

    MIN_PASSWORD_LENGTH = useMemo(() => appState.minPasswordLength, [appState.minPasswordLength]);

    const [saveData] = useMutation(CREATE_USER_MUTATION);

    const [state, setState] = useSetState<DefaultState>(INITIAL_STATE);
    const [fields, errors, onFieldChange, , validate] = useForm({
        fields: {
            firstName: appState?.user?.FirstName ?? '',
            surname: appState?.user?.Surname ?? '',
            email: appState?.user?.Email ?? '',
            password: '',
            repeatPassword: '',
        },
        validator,
    });

    const signUpButtonRef = useRef<any>(null);
    const signUpButtonDimensions = useElementDimensions(signUpButtonRef.current);

    useEffect(() => {
        if (state.isVisibleErrorMessage) {
            setState({ socialButtonKey: Math.random() });
        }
    }, [state.isVisibleErrorMessage, setState]);

    const submitSignUpForm = useCallback(() => {
        setState({ errorMessage: '', isVisibleErrorMessage: false });

        if (validate()) {
            setState({ isSubmitting: true });
            saveData({
                variables: {
                    authType: 'password',
                    email: fields.email,
                    password: fields.password,
                    repeatPassword: fields.repeatPassword,
                    firstName: fields.firstName,
                    surname: fields.surname,
                }
            }).then(() => {
                login(fields.email, fields.password).then(() => {
                    appState.getUser().then((user: HomeUser) => {
                        setState({ isSubmitting: false });
                        setAppState({
                            isChangingEmail: false,
                        });
                        redirectAfterSignUp(user);
                    });
                })
            }).catch((error) => {
                setState({ isSubmitting: false, errorMessage: errorParser(error), isVisibleErrorMessage: true });
                setTimeout(() => document.getElementById("errorMessage")?.scrollIntoView());
            });
        }

    }, [setState, validate, saveData, fields, appState, setAppState, redirectAfterSignUp]);

    const onSocialSignup = async (socialProfile: any, socialLoginType: SocialLoginType) => {
        setState({ isSubmitting: true });

        try {
            let email: string = '';
            let firstName: string = '';
            let surname: string = '';
            const credential = socialProfile["credential"];
            if (socialLoginType === "google") {
                const decodedData: any = jwt_decode(credential);
                email = decodedData.email;
                firstName = decodedData.given_name;
                surname = decodedData.family_name;

                storage.persist('socialFirstName', firstName);
                storage.persist('socialLastName', surname);
            } else {
                email = socialProfile.email;
            }
            // register account
            await saveData({
                variables: {
                    authType: 'social',
                    email,
                    firstName,
                    surname,
                }
            });

            // log the user in
            await socialLogin(
                email,
                credential,
                socialLoginType
            );

            // load user and redirect
            appState.getUser().then((user: HomeUser) => {
                setState({ isSubmitting: false });
                redirectAfterSignUp(user);
            });
        } catch (e) {
            setState({ isSubmitting: false, errorMessage: errorParser(e), isVisibleErrorMessage: true });
        }
    };

    const setAreRequirementsVisible = useCallback(() => {
        setState({ areRequirementsVisible: true });
    }, [setState]);

    const needToSignUpAgain = appState?.authorized && needEmailVerification(appState?.user);

    return (
        <div css={authContent}>
            {state.isSubmitting && <Loading onTop overlay overlayColour={"white"} />}
            <div css={leftHeader}>Create Account</div>
            <ErrorMessage
                isVisible={state.isVisibleErrorMessage}
                message={state.errorMessage}
                css={errorMessageStyle}
            />
            <div css={formContainerStyle}>
                <Form onSubmit={submitSignUpForm}>
                    <TwoColumn>
                        <LabelledField name="name" error={errors.firstName} label="First Name">
                            <Input
                                hasError={!!errors.firstName}
                                css={nameInput}
                                label="Type First Name" name="firstName"
                                onChange={onFieldChange}
                                value={fields.firstName}
                            />
                        </LabelledField>
                        <LabelledField name="surname" error={errors.surname} label="Last Name">
                            <Input
                                hasError={!!errors.surname}
                                css={nameInput}
                                label="Type Last Name"
                                name="surname"
                                onChange={onFieldChange}
                                value={fields.surname}
                            />
                        </LabelledField>
                    </TwoColumn>
                    <LabelledField name="email" error={errors.email} label="Email">
                        <Input
                            hasError={!!errors.email}
                            css={emailInput}
                            label="Enter email address"
                            name="email"
                            onChange={onFieldChange}
                            value={fields.email}
                            {...needToSignUpAgain && { autoFocus: true }}
                        />
                    </LabelledField>
                    <LabelledField name="password" error={errors.password} label="Password">
                        <Input
                            hasError={!!errors.password}
                            css={passwordInput}
                            label="Password"
                            type="password"
                            name="password"
                            onChange={onFieldChange}
                            onFocus={setAreRequirementsVisible}
                            value={fields.password}
                        />
                    </LabelledField>
                    {state.areRequirementsVisible && (
                        <div css={passwordRequirements}>
                            <p>Password must:</p>
                            <ul>
                                <li css={passwordRequirement(fields.password, !!errors.password)}>
                                    Have at least {MIN_PASSWORD_LENGTH} characters
                                </li>
                            </ul>
                        </div>
                    )}
                    <LabelledField name="repeatPassword" error={errors.repeatPassword} label="Confirm Password">
                        <Input
                            hasError={!!errors.repeatPassword}
                            css={passwordInput}
                            label="Confirm password"
                            type="password"
                            name="repeatPassword"
                            onChange={onFieldChange}
                            value={fields.repeatPassword}
                        />
                    </LabelledField>
                    <div css={formActions}>
                        <FormActions customCss={customFormActionsStyles}>
                            <Button
                                css={formSubmitButton}
                                type="submit"
                                forceLoading={state.isSubmitting}
                                ref={signUpButtonRef}
                            >
                                Sign Up
                            </Button>
                        </FormActions>
                    </div>
                    <div css={lineContainerStyle}>
                        <div css={lineStyle} />
                        <span css={lineTextStyle}>or</span>
                        <div css={lineStyle} />
                    </div>
                    <SocialLogin socialSignUp={onSocialSignup} buttonWidth={signUpButtonDimensions.width} isSignUp />
                    {!needToSignUpAgain && (
                        <div css={signInText}>
                            Already have an account? Please <a href={LOGIN_URL}>sign in</a>.
                        </div>
                    )}
                </Form>
            </div>
        </div>
    );
};

const isPasswordLongEnough = (password: string): boolean => (
    trim(password).length >= MIN_PASSWORD_LENGTH
);

const validator: any = (fields: FieldList, isSubmit: boolean = false) => {
    const errors: any = {};

    if (!fields.firstName) {
        errors.firstName = 'Please provide your first name';
    }

    if (!fields.surname) {
        errors.surname = 'Please provide your last name';
    }

    if (!fields.email) {
        errors.email = 'Please provide your email';
    }

    if (fields.email && !EmailValidator.validate(fields.email) && isSubmit) {
        errors.email = 'Please provide a valid email address';
    }

    if (!fields.password) {
        errors.password = 'Please provide a password';
    }

    if (!fields.repeatPassword) {
        errors.repeatPassword = 'Please provide the confirm password';
    }

    if (isSubmit && fields.repeatPassword !== fields.password) {
        errors.repeatPassword = "Your passwords do not match";
    }

    return errors;
};

const CREATE_USER_MUTATION = gql`
    mutation ($firstName: String!, $authType: String!, $surname: String!, $email: String!, $password: String, $repeatPassword: String) {
        createHomeUser(FirstName: $firstName, AuthType: $authType, Surname: $surname, Email: $email, Password: $password, RepeatPassword: $repeatPassword) {
            id
        }
    }
`;

export default SignUp;

const lineContainerStyle = css`
    display: flex;

    ${theme.breakpoints.up("5xl")} {
        margin: 68px 0;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin-bottom: 38px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin-bottom: 28px;
    }

    ${theme.breakpoints.only('md')} {
        margin-bottom: 21px;
    }

    ${theme.breakpoints.only("sm")} {
        margin-bottom: 25px;
    }

    ${theme.breakpoints.down("xs")} {
        margin-bottom: 18px;
    }
`;

const lineTextStyle = css`
    padding: 0 28px;
    margin-top: -8px;
    color: ${theme.colours.silverChalice2};

    ${theme.breakpoints.up("5xl")} {
        font-size: 18px;
        margin-top: -10px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        font-size: 14px;
    }

    ${theme.breakpoints.down("xl")} {
        font-size: 11px;
        margin-top: -6px;
    }
`;

const lineStyle = css`
    height: 1px;
    flex-grow: 1;
    background: ${theme.colours.silverChalice2};
`;

const authContent = css`
    margin-top: 40px;
    width: 510px;
    position: relative;

    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }

    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }
`;

const formContainerStyle = css`
    margin-top: 12px;
`;

const nameInput = css`
    margin-top: 0;
    box-sizing: border-box;
`;

const emailInput = css`
    margin-top: 0;
    box-sizing: border-box;
    width: 100%;
`;

const passwordInput = css`
    margin-top: 0;
    box-sizing: border-box;
    width: 100%;
`;

const passwordRequirements = css`
    margin-bottom: 19px;

    p {
        margin-bottom: 10px;
        font-size: 14px;
    }
`;

const passwordRequirement = (password: string, fieldHasError: boolean) => css`
    font-size: 14px;
    list-style: disc inside;
    color: ${
            (password === '' && !fieldHasError)
                    ? 'black'
                    : isPasswordLongEnough(password) ? theme.colours.killarney : theme.colours.crimson
    }
`;

const signInText = css`
    font-size: 14px;
    font-weight: 100;
    text-align: center;

    a {
        text-decoration: none;
        color: ${theme.colours.blue['400']}
    }
`;

const formActions = css`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    ${theme.breakpoints.up("5xl")} {
        margin: 63px 0 68px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin: 41px 0 45px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin: 29px 0 34px;
    }

    ${theme.breakpoints.only("md")} {
        margin: 21px 0 26px;
    }

    ${theme.breakpoints.only("sm")} {
        margin: 26px 0 31px;
    }

    ${theme.breakpoints.down("xs")} {
        margin: 19px 0 24px;
    }

    ${theme.breakpoints.down('md')} {
        margin-top: 20px;
        justify-content: center;
    }
`;

const customFormActionsStyles = css`
    width: 100%;
    justify-content: center;
`;

const formSubmitButton = css`
    background-color: ${theme.colours.blue['400']};
    width: 400px;
    border-radius: 45px;
    height: 40px;
    padding: 0;

    ${theme.breakpoints.down('xs')} {
        width: 250px;
    }
`;

const leftHeader = css`
    color: ${theme.colours.curiousBlue};
    font-weight: ${theme.fonts.weights.bold};

    ${theme.breakpoints.up("xxl")} {
        font-size: 50px;
        line-height: 75px;
    }

    ${theme.breakpoints.between("md", "xl")} {
        font-size: 43px;
        line-height: 65px;
    }

    ${theme.breakpoints.down("sm")} {
        font-size: 30px;
        line-height: 45px;
    }
`;

const errorMessageStyle = css`
    margin-top: 0;
    margin-bottom: 16px;
`;
